import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared, type ErrorView } from '@krp/client-runtime'

import { normalizeRid } from '../../../shared/model/scopeKeys'
import { DEFAULT_TEMPLATE, clampLabelCopies, templateOf } from './templates'
import type { PriceTagItem, PriceTagScanState, PriceTagSelection, PriceTagsState } from './types'

/**
 * Подбор ценников по базам (срок `device`, пишется реестром хранения по
 * пользователю). Подбор набирают сканером, обходя полки: уход в другое окно
 * или F5 не должны стирать набранное. Сюда же приходят товары, отмеченные в
 * инвентаризации «перепечатать», — ради этого ту отметку и ставят.
 */
const initialState: PriceTagsState = { byResource: {} }

const emptySelection = (): PriceTagSelection => ({
  warehouseRefKey: '',
  warehouseName: '',
  templateId: DEFAULT_TEMPLATE.id,
  items: [],
})

function selectionOf(state: PriceTagsState, resourceId: string): PriceTagSelection {
  const rid = normalizeRid(resourceId)
  state.byResource[rid] ??= emptySelection()
  return state.byResource[rid]
}

function chooseWarehouse(selection: PriceTagSelection, refKey: string, name: string) {
  const key = refKey.toLowerCase()
  if (selection.warehouseRefKey === key) return
  selection.warehouseRefKey = key
  selection.warehouseName = name
  // Цены в подборе — цены прежнего склада; печатать их со ссылкой на новый — напечатать неправду.
  selection.items = []
}

/**
 * Товар в подбор. Повторный скан того же товара НЕ плодит строку, а добавляет
 * ценник: на полке сканируют по одному, а печатают пачкой. Цена — свежая.
 */
function addItem(selection: PriceTagSelection, item: PriceTagItem) {
  const key = item.productRefKey.toLowerCase()
  const existing = selection.items.find((row) => row.productRefKey === key)
  if (existing === undefined) {
    selection.items.unshift({ ...item, productRefKey: key, copies: clampLabelCopies(item.copies) })
    return
  }
  existing.copies = clampLabelCopies(existing.copies + item.copies)
  // Цена из обхода (время неизвестно) не затирает свежий скан.
  if (item.pricedAt === null && existing.pricedAt !== null) return
  existing.price = item.price
  existing.pricedAt = item.pricedAt
}

const priceTagsSlice = createSlice({
  name: 'onec/priceTags',
  initialState,
  reducers: {
    priceTagWarehouseChosen(state, action: PayloadAction<{ resourceId: string; refKey: string; name: string }>) {
      chooseWarehouse(selectionOf(state, action.payload.resourceId), action.payload.refKey, action.payload.name)
    },
    priceTagTemplateChosen(state, action: PayloadAction<{ resourceId: string; templateId: string }>) {
      selectionOf(state, action.payload.resourceId).templateId = templateOf(action.payload.templateId).id
    },
    /** Скан дошёл: только если склад с тех пор не сменили — иначе цена чужого склада. */
    priceTagAdded(state, action: PayloadAction<{ resourceId: string; warehouseRefKey: string; item: PriceTagItem }>) {
      const selection = selectionOf(state, action.payload.resourceId)
      if (selection.warehouseRefKey !== action.payload.warehouseRefKey.toLowerCase()) return
      addItem(selection, action.payload.item)
    },
    /** Отмеченные в инвентаризации — в подбор того же склада этой базы. */
    priceTagsPicked(
      state,
      action: PayloadAction<{
        resourceId: string
        warehouse: { refKey: string; name: string }
        items: readonly Omit<PriceTagItem, 'copies' | 'pricedAt'>[]
      }>,
    ) {
      const selection = selectionOf(state, action.payload.resourceId)
      chooseWarehouse(selection, action.payload.warehouse.refKey, action.payload.warehouse.name)
      // Цена — снимок скана из сессии обхода, когда он сделан — неизвестно: не выдавать за «сейчас».
      for (const item of action.payload.items) addItem(selection, { ...item, copies: 1, pricedAt: null })
    },
    priceTagCopiesChanged(state, action: PayloadAction<{ resourceId: string; productRefKey: string; copies: number }>) {
      const item = selectionOf(state, action.payload.resourceId).items.find(
        (row) => row.productRefKey === action.payload.productRefKey.toLowerCase(),
      )
      if (item !== undefined) item.copies = clampLabelCopies(action.payload.copies)
    },
    priceTagRemoved(state, action: PayloadAction<{ resourceId: string; productRefKey: string }>) {
      const selection = selectionOf(state, action.payload.resourceId)
      selection.items = selection.items.filter((row) => row.productRefKey !== action.payload.productRefKey.toLowerCase())
    },
    priceTagsCleared(state, action: PayloadAction<{ resourceId: string }>) {
      selectionOf(state, action.payload.resourceId).items = []
    },
    /** Восстановление из реестра хранения: подбор целиком. */
    priceTagsRestored(_state, action: PayloadAction<PriceTagsState['byResource']>) {
      return { byResource: action.payload }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      delete state.byResource[normalizeRid(action.payload.resourceId)]
    })
  },
})

export const {
  priceTagWarehouseChosen,
  priceTagTemplateChosen,
  priceTagAdded,
  priceTagsPicked,
  priceTagCopiesChanged,
  priceTagRemoved,
  priceTagsCleared,
  priceTagsRestored,
} = priceTagsSlice.actions

export const priceTagsReducer = priceTagsSlice.reducer

// --- скан (срок `screen`) ------------------------------------------------------------------------------------------

const IDLE: PriceTagScanState = { busy: false, error: null, notFound: null }

const initialScan: Record<string, PriceTagScanState> = {}

const priceTagScanSlice = createSlice({
  name: 'onec/priceTagScan',
  initialState: initialScan,
  reducers: {
    priceTagScanStarted(state, action: PayloadAction<{ resourceId: string }>) {
      state[normalizeRid(action.payload.resourceId)] = { ...IDLE, busy: true }
    },
    priceTagScanSettled(
      state,
      action: PayloadAction<{ resourceId: string; error: ErrorView | null; notFound: string | null }>,
    ) {
      const rid = normalizeRid(action.payload.resourceId)
      if (state[rid] === undefined) return
      state[rid] = { busy: false, error: action.payload.error, notFound: action.payload.notFound }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => ({}))
    builder.addCase(resourceScopeCleared, (state, action) => {
      delete state[normalizeRid(action.payload.resourceId)]
    })
  },
})

export const { priceTagScanStarted, priceTagScanSettled } = priceTagScanSlice.actions
export const priceTagScanReducer = priceTagScanSlice.reducer
export const IDLE_PRICE_TAG_SCAN = IDLE
